import { useState, useEffect, useMemo } from "react";
import { Settings, Tag } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { IconButton } from "../../primitives/IconButton";
import { Drawer } from "../../primitives/Drawer";
import {
  listItemPrices,
  createItemPrice,
  updateItemPrice,
  deleteItemPrice,
  listPriceBooks,
  type ItemPrice,
  type PriceBook,
} from "../../../api/catalog";
import type { Promotion, ProductVisibility } from "../../../api/catalog";

/** Formulaire du tiroir : paramètres communs (une seule fois) + montant/actif par rôle. */
type DrawerForm = {
  currency: string;
  billing_period: string | null;
  min_quantity: string;
  max_quantity: string;
  note: string;
  public: { unit_price: string; is_active: boolean };
  floor: { unit_price: string; is_active: boolean };
};

type ItemPriceManagerProps = {
  productId: string;
  /** Filtre les pricebooks affichés selon la règle métier (visibility_scope). */
  visibilityScope?: ProductVisibility;
  onUpdate?: () => void;
  promotions?: Promotion[];
};

// Public et Plancher uniquement. Les promotions sont gérées dans le module Promotions (pas de prix "Promo" ici).
const PRICE_TYPE_OPTIONS: { value: string; label: string; description: string }[] = [
  {
    value: "public",
    label: "Public",
    description: "Prix de vente standard (source officielle). Les promotions du module Promotions s'appliquent sur ce prix.",
  },
  {
    value: "floor",
    label: "Plancher",
    description: "Prix minimum autorisé (garde-fou commercial).",
  },
];

const BILLING_PERIOD_OPTIONS = [
  { value: "one_time", label: "Unique" },
  { value: "monthly", label: "Mensuel" },
  { value: "annual", label: "Annuel" },
];

/** Pricebooks à afficher selon visibility_scope : internal = interne, ambassador = ambassadeur, end_customer = public, both = ambassadeur + public. */
function filterPriceBooksByVisibility(books: PriceBook[], visibility: ProductVisibility): PriceBook[] {
  if (visibility === "internal") return books.filter((b) => b.scope === "internal");
  if (visibility === "ambassador") return books.filter((b) => b.scope === "ambassador");
  if (visibility === "end_customer") return books.filter((b) => b.scope === "public");
  if (visibility === "both") return books.filter((b) => b.scope === "ambassador" || b.scope === "public");
  return [];
}

const defaultDrawerForm: DrawerForm = {
  currency: "CAD",
  billing_period: null,
  min_quantity: "",
  max_quantity: "",
  note: "",
  public: { unit_price: "", is_active: true },
  floor: { unit_price: "", is_active: true },
};

function buildDrawerFormFromPrices(prices: ItemPrice[]): DrawerForm {
  const pub = prices.find((p) => p.price_type === "public");
  const fl = prices.find((p) => p.price_type === "floor");
  const first = pub ?? fl;
  return {
    currency: first?.currency ?? "CAD",
    billing_period: first?.billing_period ?? null,
    min_quantity: first?.min_quantity != null ? String(first.min_quantity) : "",
    max_quantity: first?.max_quantity != null ? String(first.max_quantity) : "",
    note: first?.note ?? "",
    public: {
      unit_price: pub ? String(pub.unit_price) : "",
      is_active: pub?.is_active ?? true,
    },
    floor: {
      unit_price: fl ? String(fl.unit_price) : "",
      is_active: fl?.is_active ?? true,
    },
  };
}

export function ItemPriceManager({ productId, visibilityScope = "both", onUpdate, promotions = [] }: ItemPriceManagerProps) {
  const [itemPrices, setItemPrices] = useState<ItemPrice[]>([]);
  const [priceBooks, setPriceBooks] = useState<PriceBook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerPriceBookId, setDrawerPriceBookId] = useState<string | null>(null);
  const [drawerForm, setDrawerForm] = useState<DrawerForm>(defaultDrawerForm);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerErrors, setDrawerErrors] = useState<Record<string, string>>({});

  const drawerPrices = useMemo(
    () => (drawerPriceBookId ? itemPrices.filter((p) => p.price_book_id === drawerPriceBookId) : []),
    [drawerPriceBookId, itemPrices]
  );

  const loadData = async () => {
    try {
      const [prices, books] = await Promise.all([
        listItemPrices(productId),
        listPriceBooks(),
      ]);
      setItemPrices(prices);
      setPriceBooks(
        books.filter(
          (pb) =>
            pb.name !== "promo_xlflow_launch_2026" &&
            !String(pb.name || "").toLowerCase().includes("promo_xlflow_launch")
        )
      );
    } catch (error) {
      console.error("Failed to load data:", error);
      alert(`Erreur lors du chargement: ${(error as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [productId]);

  useEffect(() => {
    if (drawerPriceBookId) {
      setDrawerForm(buildDrawerFormFromPrices(drawerPrices));
      setDrawerErrors({});
    }
  }, [drawerPriceBookId, drawerPrices]);

  const sharedPayload = useMemo(() => {
    return {
      currency: drawerForm.currency,
      billing_period: drawerForm.billing_period as string | null,
      min_quantity: drawerForm.min_quantity ? parseFloat(drawerForm.min_quantity) : null,
      max_quantity: drawerForm.max_quantity ? parseFloat(drawerForm.max_quantity) : null,
      note: drawerForm.note || null,
    };
  }, [drawerForm]);

  const handleSaveDrawer = async () => {
    if (!drawerPriceBookId) return;
    const publicNum = parseFloat(drawerForm.public.unit_price.trim());
    const floorNum = parseFloat(drawerForm.floor.unit_price.trim());
    const hasPublic = drawerForm.public.unit_price.trim() !== "" && !Number.isNaN(publicNum) && publicNum >= 0;
    const hasFloor = drawerForm.floor.unit_price.trim() !== "" && !Number.isNaN(floorNum) && floorNum >= 0;
    if (!hasPublic && !hasFloor) {
      setDrawerErrors({ form: "Définir au moins un montant (Public ou Plancher)." });
      return;
    }
    setDrawerErrors({});
    setDrawerSaving(true);
    try {
      const pubExisting = drawerPrices.find((p) => p.price_type === "public");
      const flExisting = drawerPrices.find((p) => p.price_type === "floor");

      if (hasPublic) {
        const payload = {
          price_book_id: drawerPriceBookId,
          price_type: "public",
          unit_price: parseFloat(drawerForm.public.unit_price),
          ...sharedPayload,
          is_active: drawerForm.public.is_active,
        };
        if (pubExisting) {
          await updateItemPrice(productId, pubExisting.id, payload);
        } else {
          await createItemPrice(productId, payload);
        }
      } else if (pubExisting) {
        await deleteItemPrice(productId, pubExisting.id);
      }

      if (hasFloor) {
        const payload = {
          price_book_id: drawerPriceBookId,
          price_type: "floor",
          unit_price: parseFloat(drawerForm.floor.unit_price),
          ...sharedPayload,
          is_active: drawerForm.floor.is_active,
        };
        if (flExisting) {
          await updateItemPrice(productId, flExisting.id, payload);
        } else {
          await createItemPrice(productId, payload);
        }
      } else if (flExisting) {
        await deleteItemPrice(productId, flExisting.id);
      }

      await loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to save prices:", error);
      setDrawerErrors({ form: (error as Error).message || "Erreur lors de l'enregistrement." });
    } finally {
      setDrawerSaving(false);
    }
  };

  if (isLoading) {
    return <div className="p-4 text-sm text-muted">Chargement...</div>;
  }

  const visiblePriceBooks = filterPriceBooksByVisibility(priceBooks, visibilityScope);
  const pricesByBook = visiblePriceBooks.map((book) => ({
    book,
    prices: itemPrices.filter((p) => p.price_book_id === book.id),
  }));

  const drawerBook = drawerPriceBookId ? priceBooks.find((b) => b.id === drawerPriceBookId) : null;
  const promosForDrawerBook =
    drawerPriceBookId && promotions.length > 0
      ? promotions.filter((p) => (p.target_price_book_ids || []).includes(drawerPriceBookId))
      : [];

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-text">Prix par PriceBook</h4>
      {visibilityScope === "internal" ? (
        <p className="text-xs text-muted">Catalogue interne uniquement. Configurez le prix dans le pricebook ci‑dessous (non exposé aux ambassadeurs ni au public).</p>
      ) : (
        <p className="text-xs text-muted">Cliquez sur Paramètres pour ajuster les prix par rôle (public, plancher) et voir les promotions appliquées.</p>
      )}
      {visiblePriceBooks.length === 0 && visibilityScope === "internal" ? (
        <p className="text-sm text-muted">Aucun pricebook interne disponible. Créez le pricebook « internal » (scope: internal) dans l&apos;administration si besoin.</p>
      ) : (
        pricesByBook.map(({ book, prices }) => {
          const pub = prices.find((p) => p.price_type === "public");
          const floor = prices.find((p) => p.price_type === "floor");
          const currency = pub?.currency ?? floor?.currency ?? "CAD";
          const formatPrice = (p: ItemPrice | undefined) =>
            p != null && p.unit_price != null
              ? `${Number(p.unit_price).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${p.currency ?? currency}`
              : "—";
          return (
            <div key={book.id} className="rounded-md border border-border bg-surface p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-medium text-text">{book.name}</h5>
                  <p className="text-xs text-muted">Scope: {book.scope}</p>
                  {prices.length === 0 ? (
                    <p className="text-xs text-muted mt-1">Aucun prix défini</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                      <span className="text-text">
                        <span className="text-muted font-normal">Public :</span>{" "}
                        <span className="font-medium">{formatPrice(pub)}</span>
                        {pub && !pub.is_active && (
                          <span className="ml-1 text-xs text-muted">(inactif)</span>
                        )}
                      </span>
                      <span className="text-text">
                        <span className="text-muted font-normal">Plancher :</span>{" "}
                        <span className="font-medium">{formatPrice(floor)}</span>
                        {floor && !floor.is_active && (
                          <span className="ml-1 text-xs text-muted">(inactif)</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <span title="Paramètres" className="inline-flex shrink-0">
                  <IconButton
                    icon={<Settings size={16} />}
                    ariaLabel="Paramètres"
                    intent="primary"
                    size="sm"
                    onClick={() => setDrawerPriceBookId(book.id)}
                  />
                </span>
              </div>
            </div>
          );
        })
      )}

      <Drawer
        title={drawerBook ? `Prix — ${drawerBook.name}` : "Prix"}
        open={!!drawerPriceBookId}
        onClose={() => setDrawerPriceBookId(null)}
        size="lg"
      >
        {drawerBook && (
          <div className="space-y-6">
            {/* Paramètres communs (une seule fois par pricebook) */}
            <section>
              <h5 className="text-sm font-semibold text-text mb-3">Paramètres communs</h5>
              <p className="text-xs text-muted mb-3">
                Devise, période de facturation, quantités et note s&apos;appliquent aux deux rôles (Public et Plancher).
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Devise</label>
                  <Input
                    value={drawerForm.currency}
                    onChange={(e) => setDrawerForm((f) => ({ ...f, currency: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Période de facturation</label>
                  <Select
                    options={["Aucune", ...BILLING_PERIOD_OPTIONS.map((opt) => opt.label)]}
                    value={
                      drawerForm.billing_period
                        ? BILLING_PERIOD_OPTIONS.find((opt) => opt.value === drawerForm.billing_period)?.label ?? "Aucune"
                        : "Aucune"
                    }
                    onChange={(value) => {
                      if (value === "Aucune") {
                        setDrawerForm((f) => ({ ...f, billing_period: null }));
                      } else {
                        const option = BILLING_PERIOD_OPTIONS.find((opt) => opt.label === value);
                        if (option) setDrawerForm((f) => ({ ...f, billing_period: option.value }));
                      }
                    }}
                  />
                </div>
                <Input
                  label="Quantité min (optionnel)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={drawerForm.min_quantity}
                  onChange={(e) => setDrawerForm((f) => ({ ...f, min_quantity: e.target.value }))}
                />
                <Input
                  label="Quantité max (optionnel)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={drawerForm.max_quantity}
                  onChange={(e) => setDrawerForm((f) => ({ ...f, max_quantity: e.target.value }))}
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-text mb-1">Note (optionnel)</label>
                <textarea
                  className="w-full h-20 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                  value={drawerForm.note}
                  onChange={(e) => setDrawerForm((f) => ({ ...f, note: e.target.value }))}
                />
              </div>
            </section>

            {/* Montants par rôle (seule différence Public / Plancher) */}
            <section>
              <h5 className="text-sm font-semibold text-text mb-3">Montants par rôle</h5>
              <p className="text-xs text-muted mb-3">
                Les promotions s&apos;appliquent sur le prix public. Définir au moins un montant.
              </p>
              <div className="space-y-3">
                {PRICE_TYPE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className="flex flex-wrap items-center gap-3 rounded border border-border bg-surface-muted p-3"
                  >
                    <div className="min-w-[100px]">
                      <span className="text-sm font-medium text-text">{opt.label}</span>
                      <p className="text-xs text-muted">{opt.description}</p>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Montant"
                        value={opt.value === "public" ? drawerForm.public.unit_price : drawerForm.floor.unit_price}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDrawerForm((f) => ({
                            ...f,
                            [opt.value]: { ...f[opt.value as "public" | "floor"], unit_price: v },
                          }));
                        }}
                      />
                      <span className="text-sm text-muted">{drawerForm.currency}</span>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-text">
                      <input
                        type="checkbox"
                        checked={opt.value === "public" ? drawerForm.public.is_active : drawerForm.floor.is_active}
                        onChange={(e) =>
                          setDrawerForm((f) => ({
                            ...f,
                            [opt.value]: { ...f[opt.value as "public" | "floor"], is_active: e.target.checked },
                          }))
                        }
                        className="h-4 w-4 rounded border-border"
                      />
                      Actif
                    </label>
                  </div>
                ))}
              </div>
              {drawerErrors.form && (
                <p className="mt-2 text-sm text-danger">{drawerErrors.form}</p>
              )}
              <div className="mt-4 flex justify-end">
                <Button
                  label={drawerSaving ? "Enregistrement…" : "Enregistrer"}
                  intent="primary"
                  size="sm"
                  onClick={handleSaveDrawer}
                  disabled={drawerSaving}
                />
              </div>
            </section>

            <section>
              <h5 className="text-sm font-semibold text-text mb-2 flex items-center gap-2">
                <Tag size={14} className="text-primary" />
                Promotions appliquées
              </h5>
              {promosForDrawerBook.length === 0 ? (
                <p className="text-xs text-muted">Aucune promotion active ne cible ce PriceBook pour ce produit.</p>
              ) : (
                <ul className="space-y-2">
                  {promosForDrawerBook.map((promo) => (
                    <li key={promo.id} className="rounded border border-border bg-surface-muted/50 p-2 text-sm">
                      <span className="font-medium text-text">{promo.name}</span>
                      {promo.code && (
                        <span className="ml-2 text-xs text-primary">Code: {promo.code}</span>
                      )}
                      <span className="block text-xs text-muted mt-0.5">
                        {promo.promotion_type === "percentage"
                          ? `${promo.value}%`
                          : promo.promotion_type === "fixed_amount"
                          ? `${promo.value} CAD réduction`
                          : `Prix fixe ${promo.value} CAD`}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </Drawer>
    </div>
  );
}
